import { useEffect, useMemo, useRef, useState } from 'react';
import Autorenew from '@mui/icons-material/Autorenew';
import PlusIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { Link } from 'react-router';
import { AiChip } from '@/components/shared/ai-chip';
import { BaseDialog } from '@/components/shared/base-dialog';
import { ContentStream } from '@/components/shared/content-stream';
import { MarkdownRenderer } from '@/components/shared/markdown-renderer';
import { PulsingDot } from '@/components/shared/pulsing-dot';
import {
  FeedbackSkillsStreamingEvent,
  useFeedbackSkillsStreaming,
} from '@/context/channels/guidance-feedback/streaming';
import { useNotification } from '@/context/notification';
import { useShowUserFeedback } from '@/data/guidance/feedback';
import { useEditUserPrioritySkillSets } from '@/data/taxonomy/user-priority-skill-sets';
import { useSkills } from '@/packages/skill-management/context/skills';
import type { CheckedSkill } from '@/packages/skill-management/context/types';
import type {
  UpdateSkillsDialogProps,
  StreamingDescriptionProps,
  SkillsAreaProps,
  DialogFooterActionsProps,
} from './types';

const MIN_LOCAL_SELECTED_SKILLS_LENGTH = 2;
const SUMMARY_TOKEN_DELAY_RANGE: [number, number] = [15, 25];

const sortSkills = (skills: CheckedSkill[]) => {
  const checked = skills.filter((s) => s.checked);
  const unchecked = skills
    .filter((s) => !s.checked)
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  return [...checked, ...unchecked];
};

const StreamingDescription = ({
  isStreamingError,
  summary,
  onSummaryDisplayed,
}: StreamingDescriptionProps) => {
  if (isStreamingError) {
    return (
      <Typography color="error">
        Infelizmente não conseguimos gerar as habilidades com as informações
        fornecidas. Tente novamente editando o conteúdo do feedback.
      </Typography>
    );
  }

  if (!summary) return <PulsingDot testId="summary-pulsing-dot" />;

  return (
    <ContentStream
      content={summary}
      splitBy="word"
      tokenDelayRange={SUMMARY_TOKEN_DELAY_RANGE}
      onEnd={onSummaryDisplayed}
    >
      {({ markdownContent }) => <MarkdownRenderer markdown={markdownContent} />}
    </ContentStream>
  );
};

const SkillsArea = ({
  isWaitingForSelectedSkills,
  hasDisplayedSummary,
  skills,
  onToggle,
}: SkillsAreaProps) => {
  if (!isWaitingForSelectedSkills) {
    return (
      <Stack
        direction="row"
        gap={2}
        flexWrap="wrap"
        maxHeight={{ md: 500 }}
        overflow="auto"
        mt={2}
        sx={{
          animation: 'skills-fade-in 400ms ease-out',
          '@keyframes skills-fade-in': {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        }}
      >
        {skills.map((skill) => (
          <Chip
            key={skill.id}
            label={skill.skill.name_pt}
            variant={skill.checked ? 'filled' : 'outlined'}
            onClick={() => onToggle(skill.id)}
            sx={{ cursor: 'pointer' }}
            icon={
              skill.checked ? (
                <CheckIcon fontSize="small" />
              ) : (
                <PlusIcon fontSize="small" />
              )
            }
          />
        ))}
      </Stack>
    );
  }

  // Wait for the summary to be fully typed out on screen before showing this
  // dot too — otherwise both placeholders blink on screen at once.
  if (!hasDisplayedSummary) return null;

  return (
    <Box mt={2}>
      <PulsingDot testId="skills-pulsing-dot" />
    </Box>
  );
};

const DialogFooterActions = ({
  isStreamingError,
  onRetry,
  onClose,
  onConfirm,
  canCancel,
  canConfirm,
  isConfirming,
}: DialogFooterActionsProps) => {
  if (isStreamingError) {
    return (
      <Button variant="contained" onClick={onRetry} startIcon={<Autorenew />}>
        Tentar novamente
      </Button>
    );
  }

  return (
    <>
      <Button variant="text" onClick={onClose} disabled={!canCancel}>
        Cancelar
      </Button>
      <Button
        variant="contained"
        onClick={onConfirm}
        disabled={!canConfirm}
        loading={isConfirming}
      >
        Confirmar
      </Button>
    </>
  );
};

export const UpdateSkillsDialog = ({
  title = 'Seleção de habilidades',
  description,
  skills,
  onClose,
  feedbackId,
  onRetry,
}: UpdateSkillsDialogProps) => {
  const { selectedSkills, refetchSelectedSkills } = useSkills();
  const { refetch: refetchUserFeedback } = useShowUserFeedback();
  const { mutate: updateSkills, isPending } = useEditUserPrioritySkillSets();
  const { toast } = useNotification();
  const [hasSyncedAfterCompletion, setHasSyncedAfterCompletion] =
    useState(false);
  const [hasDisplayedSummary, setHasDisplayedSummary] = useState(false);
  const hasStartedRef = useRef(false);
  const [localSkills, setLocalSkills] = useState<CheckedSkill[]>(() =>
    sortSkills(skills)
  );
  const { status, summary, startStreaming } = useFeedbackSkillsStreaming({
    onCompleted: async () => {
      await refetchUserFeedback();
      await refetchSelectedSkills?.();
      setHasSyncedAfterCompletion(true);
    },
  });

  const isStreamingMode = typeof feedbackId === 'number';
  const isStreamingError =
    isStreamingMode && status === FeedbackSkillsStreamingEvent.Error;
  const isWaitingForSelectedSkills =
    isStreamingMode && !isStreamingError && !hasSyncedAfterCompletion;

  const checkedLocalSkills = localSkills.filter((s) => s.checked);
  const canSave = checkedLocalSkills?.length >= 2;
  const canConfirm = canSave && !isWaitingForSelectedSkills;
  const canCancel = isWaitingForSelectedSkills || canSave;

  const neededSkillsLength = Math.max(
    0,
    MIN_LOCAL_SELECTED_SKILLS_LENGTH - checkedLocalSkills?.length
  );
  const showNeededSkillsWarning =
    !isWaitingForSelectedSkills && !isStreamingError && neededSkillsLength > 0;

  const skillsToUpdate = useMemo(() => {
    const localSkillIds = localSkills.map((s) => s.skill.id);
    const otherSelectedSkills = (selectedSkills || [])?.filter(
      (s) => !localSkillIds.includes(s.skill.id)
    );

    return [...checkedLocalSkills, ...otherSelectedSkills];
  }, [checkedLocalSkills, selectedSkills, localSkills]);

  const toggleSkill = (skillId: number) => {
    setLocalSkills((prev) =>
      prev.map((skill) =>
        skill.id === skillId ? { ...skill, checked: !skill.checked } : skill
      )
    );
  };

  const handleConfirm = () => {
    if (!canConfirm) return;

    updateSkills(skillsToUpdate, {
      onSuccess: () => {
        toast({
          content: 'Suas habilidades foram atualizadas!',
          type: 'success',
        });
        onClose();
      },
      onError: () => {
        toast({
          content: 'Ocorreu um erro ao atualizar suas habilidades.',
          type: 'error',
        });
      },
    });
  };

  const handleRetry = () => (onRetry ?? onClose)();

  useEffect(() => {
    if (feedbackId && !hasStartedRef.current) {
      hasStartedRef.current = true;
      startStreaming(feedbackId);
    }
  }, [feedbackId, startStreaming]);

  useEffect(() => {
    if (isWaitingForSelectedSkills) return;
    setLocalSkills(sortSkills(skills));
  }, [isWaitingForSelectedSkills, skills]);

  return (
    <BaseDialog
      onClose={onClose}
      open
      maxWidth="md"
      title={
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="h2">{title}</Typography>
          <AiChip />
        </Stack>
      }
    >
      <Typography mt={1} component="div">
        {isStreamingMode ? (
          <StreamingDescription
            isStreamingError={isStreamingError}
            summary={summary}
            onSummaryDisplayed={() => setHasDisplayedSummary(true)}
          />
        ) : (
          description
        )}
      </Typography>

      <SkillsArea
        isWaitingForSelectedSkills={isWaitingForSelectedSkills}
        hasDisplayedSummary={hasDisplayedSummary}
        skills={localSkills}
        onToggle={toggleSkill}
      />

      {showNeededSkillsWarning && (
        <Stack direction="row" alignItems="center" gap={1} height={24} mt={3}>
          <ErrorOutlineIcon color="error" />
          <Typography variant="body2" color="error.main">
            Você precisa ter pelo menos mais {neededSkillsLength}{' '}
            {neededSkillsLength === 1
              ? 'habilidade selecionada'
              : 'habilidades selecionadas'}
            .
          </Typography>
        </Stack>
      )}

      <Stack
        direction={{ md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'center' }}
        gap={2}
        mt={4}
      >
        <Typography variant="body2" color="text.secondary">
          Algumas destas informações foram geradas por inteligência artificial.
          Se vir algo errado,{' '}
          <Link to="https://wa.me/5511987566079" target="_blank">
            nos avise
          </Link>
          .
        </Typography>
        <Stack
          direction="row"
          alignContent="center"
          justifyContent="flex-end"
          gap={2}
        >
          <DialogFooterActions
            isStreamingError={isStreamingError}
            onRetry={handleRetry}
            onClose={onClose}
            onConfirm={handleConfirm}
            canCancel={canCancel}
            canConfirm={canConfirm}
            isConfirming={isPending}
          />
        </Stack>
      </Stack>
    </BaseDialog>
  );
};
